import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";

const LINKS = [
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/resources", label: "Resources" },
  { to: "/blog", label: "Blog" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`glass-header sticky top-0 z-50 transition-shadow duration-300 ${scrolled ? "shadow-soft" : ""}`}
    >
      <div className="container-site flex h-20 items-center justify-between">
        <Link to="/" aria-label="Evol & Troppus home" data-testid="nav-home-link">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `rounded-full px-4 py-2 text-sm font-medium transition-colors duration-300 ${
                  isActive
                    ? "text-terracotta-500"
                    : "text-foreground/80 hover:text-terracotta-500"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <ThemeToggle />
          <Link to="/book" data-testid="nav-book-button" className="btn-primary hidden !px-6 !py-2.5 sm:inline-flex">
            Book Now
          </Link>
          <button
            data-testid="mobile-menu-button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <nav
          data-testid="mobile-menu"
          className="border-t border-border bg-background px-5 pb-8 pt-4 lg:hidden"
          aria-label="Mobile"
        >
          <div className="flex flex-col gap-1">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `rounded-xl px-4 py-3 text-base font-medium transition-colors duration-300 ${
                    isActive ? "bg-muted text-terracotta-500" : "text-foreground/85 hover:bg-muted"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/book" data-testid="mobile-nav-book-button" className="btn-primary mt-3 justify-center">
              Book a Consultation
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}
